"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  PasswordResetContextType,
  PasswordResetUser,
} from "@/types/passwordReset";
import { RecaptchaResponse } from "@/types/recaptcha";
import { PasswordResetValidation, ValidationError } from "@/types/validation";

import { httpRequest } from "@/lib/http";

import { useRecaptcha } from "@/context/util/recaptcha";
import { useSecuritySettings } from "@/context/util/securitySettings";
import { useUi } from "@/context/util/ui";

import passwordResetAlerts from "@/components/alerts/passwordReset";

const PasswordResetContext = createContext<PasswordResetContextType>(null!);

export function PasswordResetProvider({ children }: { children: ReactNode }) {
  const { handleSetAlert, setAlerts, setLoading } = useUi();
  const { recaptchaRequest } = useRecaptcha();
  const { isAnswerValid } = useSecuritySettings();

  const [activeStep, setActiveStep] = useState<number>(-1);
  const [errors, setErrors] = useState<
    ValidationError<PasswordResetValidation>
  >({});
  const [passwordResetUser, setPasswordResetUser] =
    useState<PasswordResetUser | null>(null);
  const [showMissingQuestion, setShowMissingQuestion] =
    useState<boolean>(false);

  useEffect(() => {
    setAlerts(passwordResetAlerts.map);
  }, []);

  const getAnswerValidity = useCallback(async () => {
    const answerValidityResponse = await isAnswerValid(
      passwordResetUser?.username ?? "",
      passwordResetUser?.securityQuestion ?? {},
      passwordResetUser?.userIndicator ?? "EM",
    );

    if (answerValidityResponse && answerValidityResponse.isValid) {
      handleSetAlert(
        [2],
        passwordResetAlerts.getSecurityAnswerSuccessAlert(
          passwordResetUser?.userIndicator ?? "EM",
        ),
      );
      setActiveStep(2);
    } else if (answerValidityResponse && answerValidityResponse.isBlocked) {
      handleSetAlert(
        [1],
        passwordResetAlerts.getSecurityAnswerBlockedAlert(
          passwordResetUser?.userIndicator ?? "EM",
        ),
      );
    }
  }, [
    passwordResetUser?.username,
    passwordResetUser?.securityQuestion,
    passwordResetUser?.userIndicator,
  ]);

  const getPasswordResetUser = useCallback(() => {
    const request = async (init?: RequestInit) => {
      window.scrollTo(0, 0);
      setLoading(true);

      try {
        const url = `/account-utilities/api/password-reset/user`;

        const body = JSON.stringify(passwordResetUser);
        const headers = { ...(init?.headers ?? {}) };
        const method = "POST";

        const requestInit: RequestInit = { body, headers, method };

        const passwordResetUserData = (
          await httpRequest<PasswordResetUser | RecaptchaResponse>(
            url,
            requestInit,
          )
        ).data;

        if (!passwordResetUserData) throw new Error("Empty http response");

        if ("valid" in passwordResetUserData) {
          handleSetAlert([0], passwordResetAlerts.getRecaptchaAlert());
          return passwordResetUserData;
        }

        setPasswordResetUser((prev) => ({
          ...(prev ?? {}),
          ...passwordResetUserData,
        }));

        if (!passwordResetUserData?.securityQuestion)
          setShowMissingQuestion(true);

        setActiveStep(1);
      } catch (error) {
        const message =
          "Failed to get account information. Please try again later.";
        console.error(message, error);

        handleSetAlert(
          [0],
          passwordResetAlerts.getGeneralErrorAlert(message, error, "EM"),
        );
      } finally {
        setLoading(false);
      }
    };

    return recaptchaRequest("password_reset_lookup", request);
  }, [passwordResetUser, recaptchaRequest]);

  const setPassword = useCallback(() => {
    const request = async (init?: RequestInit) => {
      window.scrollTo(0, 0);
      setLoading(true);

      try {
        const url = `/account-utilities/api/password-reset`;

        const body = JSON.stringify(passwordResetUser);
        const headers = { ...(init?.headers ?? {}) };
        const method = "PATCH";

        const requestInit: RequestInit = { body, headers, method };

        const setPasswordResponse = (
          await httpRequest<{ message: string } | RecaptchaResponse>(
            url,
            requestInit,
          )
        ).data;

        if (!setPasswordResponse) throw new Error("Empty http response");

        if ("valid" in setPasswordResponse) {
          handleSetAlert([2], passwordResetAlerts.getRecaptchaAlert());
          return setPasswordResponse;
        }

        handleSetAlert(
          [3],
          passwordResetAlerts.getResetSuccessAlert(
            passwordResetUser?.userIndicator ?? "EM",
          ),
        );

        setActiveStep(3);
      } catch (error) {
        const message = "Failed to set your password";
        console.error(message, error);

        handleSetAlert(
          [2],
          passwordResetAlerts.getGeneralErrorAlert(
            message,
            error,
            passwordResetUser?.userIndicator ?? "EM",
          ),
        );
      } finally {
        setLoading(false);
      }
    };

    return recaptchaRequest("reset_password", request);
  }, [passwordResetUser, recaptchaRequest]);

  const setPasswordAndSecuritySettings = useCallback(() => {
    const request = async (init?: RequestInit) => {
      window.scrollTo(0, 0);
      setLoading(true);

      try {
        const url = `/account-utilities/api/password-reset`;

        const body = JSON.stringify(passwordResetUser);
        const headers = { ...(init?.headers ?? {}) };
        const method = "PUT";

        const requestInit: RequestInit = { body, headers, method };

        const setPasswordAndSecuritySettingsResponse = (
          await httpRequest<{ message: string } | RecaptchaResponse>(
            url,
            requestInit,
          )
        ).data;

        if (!setPasswordAndSecuritySettingsResponse)
          throw new Error("Empty http response");

        if ("valid" in setPasswordAndSecuritySettingsResponse) {
          handleSetAlert([2], passwordResetAlerts.getRecaptchaAlert());
          return setPasswordAndSecuritySettingsResponse;
        }

        handleSetAlert(
          [3],
          passwordResetAlerts.getResetSuccessAlert(
            passwordResetUser?.userIndicator ?? "EM",
            true,
          ),
        );

        setActiveStep(3);
      } catch (error) {
        const message = "Unable to reset your security settings and password";
        console.error(message, error);

        handleSetAlert(
          [0, 1, 2],
          passwordResetAlerts.getGeneralErrorAlert(
            message,
            error,
            passwordResetUser?.userIndicator ?? "EM",
          ),
        );
      } finally {
        setLoading(false);
      }
    };

    return recaptchaRequest("reset_password_question", request);
  }, [passwordResetUser, recaptchaRequest]);

  return (
    <PasswordResetContext.Provider
      value={{
        activeStep,
        errors,
        getAnswerValidity,
        getPasswordResetUser,
        passwordResetUser,
        setActiveStep,
        setErrors,
        setPassword,
        setPasswordAndSecuritySettings,
        setPasswordResetUser,
        showMissingQuestion,
      }}
    >
      {children}
    </PasswordResetContext.Provider>
  );
}

export function usePasswordReset() {
  const ctx = useContext(PasswordResetContext);
  if (!ctx)
    throw new Error(
      "usePasswordReset must be used within a PasswordResetContext",
    );
  return ctx;
}
